export type CellAction = "chat" | "retry" | "clear";

export interface SelectedGridCell {
  rowId: string;
  rowNumber: number | null | undefined;
  rowLabel: string;
  columnId: string;
  columnLabel: string;
  /** The revision of a populated cell; null when the cell is empty. */
  revision: number | null;
}

/**
 * Chat about cells takes at most this many cells. Clear and Retry clear at most
 * this many cells with values: the grid API takes 200 actions per request.
 */
export const MAX_ACTION_CELLS = 100;

/** Retry lists at most this many cells in its chat message. */
export const MAX_RETRY_CELLS = 1_000;

export const cellKey = (rowId: string, columnId: string) => `${rowId}\u0000${columnId}`;

export const shortLabel = (label: string) =>
  label.length > 60 ? `${label.slice(0, 57).trimEnd()}...` : label;

const rowName = (cell: SelectedGridCell) =>
  `${cell.rowNumber != null ? `Row ${cell.rowNumber} ` : "Row "}"${shortLabel(cell.rowLabel)}"`;

export const cellLines = (cells: SelectedGridCell[]) =>
  cells
    .map(
      (cell) =>
        `- ${rowName(cell)} (row_id ${cell.rowId}), column "${cell.columnLabel}" (column_id ${cell.columnId})`,
    )
    .join("\n");

/** The cells by row. Each column ID shows once, so that a list of many cells stays short. */
const cellsByRow = (cells: SelectedGridCell[]) => {
  const columns = new Map<string, string>();
  const rows = new Map<string, { first: SelectedGridCell; labels: string[] }>();
  for (const cell of cells) {
    columns.set(cell.columnId, cell.columnLabel);
    const row = rows.get(cell.rowId);
    if (row) row.labels.push(`"${cell.columnLabel}"`);
    else rows.set(cell.rowId, { first: cell, labels: [`"${cell.columnLabel}"`] });
  }
  return [
    "Columns:",
    ...[...columns].map(([columnId, label]) => `- "${label}" (column_id ${columnId})`),
    "Cells by row:",
    ...[...rows.values()].map(
      ({ first, labels }) => `- ${rowName(first)} (row_id ${first.rowId}): ${labels.join(", ")}`,
    ),
  ].join("\n");
};

const plural = (count: number) => `${count} ${count === 1 ? "cell" : "cells"}`;

/** The chat message of Retry. The page already cleared the cells that had values. */
export const retryMessage = (datasetId: string, cells: SelectedGridCell[]) => {
  const cleared = cells.filter((cell) => cell.revision != null).length;
  const state =
    cleared === 0
      ? `Fill these ${plural(cells.length)}. They are empty.`
      : cleared === cells.length
        ? `Fill these ${plural(cells.length)} again. The page cleared their old values, so they are empty now.`
        : `Fill these ${plural(cells.length)}. The page cleared the old values of ${cleared} of them, so all of them are empty now.`;
  return `${state} For each row, read it with paperclip extraction read ${datasetId} --row <row_id> --evidence full. Find each value from the sources of its row, or search for it. Write each value with paperclip extraction write ${datasetId}: rows by row_id, cells keyed by column_id, mode write with its support. When you find nothing, write mode not_found with a note. Then read the grid and tell me what changed.\n\n${cellsByRow(cells)}`;
};

/** The text that a chat message gets for the cells attached to it. */
export const cellContextText = (cells: SelectedGridCell[]) =>
  `The message is about these ${plural(cells.length)} of the dataset:\n${cellLines(cells)}`;

export interface CellChip {
  key: string;
  label: string;
  cells: SelectedGridCell[];
}

/** One chip per column and run of consecutive rows, such as "Dose · rows 2–5". */
export const cellChips = (cells: SelectedGridCell[]): CellChip[] => {
  const byColumn = new Map<string, SelectedGridCell[]>();
  for (const cell of cells) {
    byColumn.set(cell.columnId, [...(byColumn.get(cell.columnId) ?? []), cell]);
  }
  const chips: CellChip[] = [];
  for (const columnCells of byColumn.values()) {
    const sorted = [...columnCells].sort(
      (a, b) => (a.rowNumber ?? Infinity) - (b.rowNumber ?? Infinity),
    );
    let run: SelectedGridCell[] = [];
    const flush = () => {
      if (run.length === 0) return;
      const first = run[0];
      const last = run[run.length - 1];
      const rows =
        first.rowNumber == null
          ? `row "${shortLabel(first.rowLabel)}"`
          : run.length === 1
            ? `row ${first.rowNumber}`
            : `rows ${first.rowNumber}–${last.rowNumber}`;
      chips.push({
        key: run.map((cell) => cellKey(cell.rowId, cell.columnId)).join("|"),
        label: `${first.columnLabel} · ${rows}`,
        cells: run,
      });
      run = [];
    };
    for (const cell of sorted) {
      const previous = run[run.length - 1];
      const continues =
        previous != null &&
        previous.rowNumber != null &&
        cell.rowNumber != null &&
        cell.rowNumber === previous.rowNumber + 1;
      if (!continues) flush();
      run.push(cell);
    }
    flush();
  }
  return chips;
};
